import { BOOT_DISK_TYPES, SCALING_INTENT_DISK_GIB_MAX, SCALING_INTENT_DISK_GIB_MIN } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/dedicated-node-pool.ts
/**
* compute-ownership-model WS-3 (SM-3): the compiled per-app dedicated node-pool wire item.
*
* R2 of `2026-10-14-app-pools-runtime-deletion` (R-081) removed `StatusReport.dedicatedNodePools`,
* so no status report carries this item any more. The schema and type stay exported until a later
* change (R-086 item 2). Nothing in the tree produces, persists or renders this item now.
*
* Before R2 the path ran like this. The DP operator's intent-to-pool compiler
* (`dedicated-pool-compiler.ts`) built the item from app scaling intent (SM-1), clamped by the DP
* admin limits (SM-2). The operator reported it on `StatusReport.dedicatedNodePools`.
* release-engine persisted it into `release.dp_live_status.dedicated_node_pools`. provisioning-svc
* merge-patched it onto the DataPlane XR `spec.dedicatedNodePools[]`, and the composition's
* `_dedicated-node-pools.gotmpl` rendered it. Stage S1 of the plan removed the provisioning-svc
* read, and stage S3 removed the compiler. R2 removed the status member, the XR field and the
* fragment. The `dedicated_node_pools` column stays until R3 drops it (R-080).
*/
/** WS-3 (SM-3): one compiled and clamped per-app dedicated node pool. Before R2 it mirrored the
*  XRD `spec.dedicatedNodePools` items (INV-001) and the output of the operator compiler
*  (`dedicated-pool-compiler.ts`). The plan removed both, so the schema mirrors nothing now.
*
*  @deprecated `2026-09-04-app-pools`. Superseded by `appPoolNodeGroupSpecSchema` in
*  `app-pool-node-group.ts`, which packs by `(appKey, orgId, workload, nodeType)` and so
*  expresses the per-org node boundary this schema cannot. R2 (R-081) removed
*  `StatusReportSchema.dedicatedNodePools`, so no data plane reports through this schema. The tag
*  means "build nothing new on this". */
const dedicatedNodePoolSpecSchema = z.object({
	poolId: z.string().regex(/^ded-[0-9a-f]{20}$/),
	appKey: z.string().regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/).max(63),
	nodeType: z.string().regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/).max(40),
	minNodeCount: z.number().int().min(0),
	maxNodeCount: z.number().int().min(1),
	spot: z.boolean(),
	diskSizeGb: z.number().int().min(SCALING_INTENT_DISK_GIB_MIN).max(SCALING_INTENT_DISK_GIB_MAX).optional(),
	gpu: z.object({
		acceleratorType: z.string().regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/),
		count: z.number().int().min(1),
		zones: z.array(z.string().regex(/^[a-z]+-[a-z]+[0-9]+-[a-z]$/)).min(1)
	}).strict().optional(),
	/**
	* The GKE NodePool boot-disk type for this dedicated pool (hyperdisk boot-disk D2).
	*
	* Before R2, `_dedicated-node-pools.gotmpl` rendered it as `nodeConfig.diskType`, and
	* provisioning-svc's `desiredDedicatedNodePools()` overwrote it on every read with
	* `bootDiskTypeForMachineType(nodeType)` (security-audit §3). The CP computed the value and
	* never trusted it from the report. The plan removed that provisioning-svc function (stage
	* S1), the fragment and the status member (R2), so no CP code reads or overwrites this field
	* now.
	*
	* Optional: an item from before the hyperdisk change has no value (D7).
	*/
	diskType: z.enum(BOOT_DISK_TYPES).optional()
}).strict();
//#endregion
export { dedicatedNodePoolSpecSchema };

//# sourceMappingURL=dedicated-node-pool.js.map